import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IsometricCanvas } from '@/app/components/IsometricCanvas';
import { Toolbar } from '@/app/components/Toolbar';
import { PropertiesPanel } from '@/app/components/PropertiesPanel';
import { PartsLibrary } from '@/app/components/PartsLibrary';
import { HelpDialog } from '@/app/components/HelpDialog';
import { BackgroundSettingsDialog } from '@/app/components/BackgroundSettings';
import { NavigationDrawer } from '@/app/components/NavigationDrawer';
import { ConnectionDialog } from '@/app/components/ConnectionDialog';
import { TextDialog } from '@/app/components/TextDialog';
import { useAutoSave, loadSavedData } from '@/app/hooks/useAutoSave';
import {
  DrawingElement,
  IsoPoint,
  IsomPart,
  Tool,
  ViewState,
  Line,
  Shape,
  TextElement,
  Circle,
  Arc,
  Spline,
  ConnectionPoint,
  Dimension,
  AngleDimension,
  ConnectionType,
  BackgroundSettings,
  BackgroundMode,
  PartAttribute,
  ChangelogEntry,
  ViewDisplaySettings,
} from '@/app/types/cad';
import { isoPointsEqual, isoToScreen, worldToScreen, screenToWorld, screenToIso, GRID_SIZE } from '@/app/utils/isometric';
import { findCommonEndpoint, getOtherEndpoint, calculateAngle, calculateOptimalArcRadius } from '@/app/utils/angleCalculation';
import { toast } from 'sonner';
import { Checkbox } from '@/app/components/ui/checkbox';
import { useLanguage } from '@/app/contexts/LanguageContext';
import { cn } from '@/app/components/ui/utils';

export function IsoBlockEditor() {
  const { t } = useLanguage();
  // Load saved data on mount
  const initialElements = loadSavedData<DrawingElement[]>('isoblock-elements', []) || [];
  const initialAttributes = loadSavedData<PartAttribute[]>('isoblock-attributes', []) || [];
  const initialPartName = loadSavedData<string>('isoblock-partname', t('common.newPart')) || t('common.newPart');
  
  const [elements, setElements] = useState<DrawingElement[]>(initialElements);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentTool, setCurrentTool] = useState<Tool>('select');
  const [tempPoints, setTempPoints] = useState<IsoPoint[]>([]);
  const [attributes, setAttributes] = useState<PartAttribute[]>(initialAttributes);
  const [currentPartName, setCurrentPartName] = useState(initialPartName);
  const [viewState, setViewState] = useState<ViewState>(
    loadSavedData<ViewState>('isoblock-viewstate', {
      zoom: 1,
      panX: 400,
      panY: 300,
    }) || {
      zoom: 1,
      panX: 400,
      panY: 300,
    }
  );

  // View Display Settings - must be declared before useAutoSave
  const [viewDisplaySettings, setViewDisplaySettings] = useState<ViewDisplaySettings>(
    loadSavedData<ViewDisplaySettings>('isoblock-viewdisplaysettings', {
      lineWidthMultiplier: 1.0,
      connectionSizeMultiplier: 1.0,
      fontSizeMultiplier: 1.0,
      iconSizeMultiplier: 1.0,
    })
  );

  // Auto-save state to localStorage
  // NOTE: useAutoSave has been updated to include unmount protection
  useAutoSave('isoblock-elements', elements, 2000);
  useAutoSave('isoblock-attributes', attributes, 2000);
  useAutoSave('isoblock-partname', currentPartName, 2000);
  useAutoSave('isoblock-viewstate', viewState, 2000);
  useAutoSave('isoblock-viewdisplaysettings', viewDisplaySettings, 2000);

  // Connection dialog state
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [tempConnection, setTempConnection] = useState<{
    position: IsoPoint;
    label: string;
  } | null>(null);

  // Grid snap state (for all elements)
  const [snapEnabled, setSnapEnabled] = useState(true);
  
  // Auto-dimension state (for Line tool)
  const [autoCreateDimension, setAutoCreateDimension] = useState(false);

  // Part metadata state
  const [partCreatedDate, setPartCreatedDate] = useState<string | null>(null);
  const [partAuthor, setPartAuthor] = useState<string | null>(null);
  const [partChangelog, setPartChangelog] = useState<ChangelogEntry[]>([]);

  // UI State
  const [showLibrary, setShowLibrary] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showBackgroundSettings, setShowBackgroundSettings] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showProperties, setShowProperties] = useState(true);
  
  // Clipboard state
  const [clipboard, setClipboard] = useState<DrawingElement[]>([]);

  // Angle dimension tool state
  const [selectedLines, setSelectedLines] = useState<string[]>([]); // IDs of selected lines for angle dimension
  const [angleDimensionPreview, setAngleDimensionPreview] = useState<{
    vertex: IsoPoint;
    point1: IsoPoint;
    point2: IsoPoint;
    value: number;
  } | null>(null);

  // Fillet tool state
  const [trimFilletLines, setTrimFilletLines] = useState(false);

  // Tangent Arc Tool State
  const [tangentArcStart, setTangentArcStart] = useState<{
    point: IsoPoint;
    sourceLine: Line;
    isStartOfLine: boolean; 
  } | null>(null);
  const [previewElement, setPreviewElement] = useState<DrawingElement | DrawingElement[] | null>(null);

  // Clear tool state when changing tools
  useEffect(() => {
    setTempPoints([]);
    setTangentArcStart(null);
    setPreviewElement(null);
    setSelectedLines([]);
    setAngleDimensionPreview(null);
  }, [currentTool]);

  // Background settings
  const [backgroundSettings, setBackgroundSettings] = useState<BackgroundSettings>(
    loadSavedData<BackgroundSettings>('isoblock-backgroundsettings', {
      mode: 'isometric',
      visible: true,
      isometricAngle: 30,
      isometricGridSize: 200,
      isometricLineColor: '#cccccc',
      isometricLineWidth: 1,
      isometricPointColor: '#666666',
      isometricPointSize: 1.5,
      cartesianGridSize: 200,
      cartesianLineColor: '#cccccc',
      cartesianLineWidth: 1,
      cartesianShowMinorLines: true,
      cartesianMinorDivisions: 5,
      cartesianMinorLineColor: '#e0e0e0',
      dotsGridSize: 200,
      dotsColor: '#999999',
      dotsSize: 2,
      backgroundColor: '#ffffff',
    })
  );

  // Preview settings for live preview in settings dialog
  const [previewBackgroundSettings, setPreviewBackgroundSettings] = useState<BackgroundSettings>(backgroundSettings);

  // Stabilize backgroundSettings reference - only create new object when values actually change
  const stableBackgroundSettings = React.useRef(backgroundSettings);
  if (JSON.stringify(stableBackgroundSettings.current) !== JSON.stringify(backgroundSettings)) {
    stableBackgroundSettings.current = backgroundSettings;
  }

  const stablePreviewSettings = React.useRef(previewBackgroundSettings);
  if (JSON.stringify(stablePreviewSettings.current) !== JSON.stringify(previewBackgroundSettings)) {
    stablePreviewSettings.current = previewBackgroundSettings;
  }

  // Memoize the active background settings to prevent unnecessary re-renders
  const activeBackgroundSettings = React.useMemo(
    () => (showBackgroundSettings ? stablePreviewSettings.current : stableBackgroundSettings.current),
    [showBackgroundSettings, stableBackgroundSettings.current, stablePreviewSettings.current]
  );

  // Undo/Redo state
  const [history, setHistory] = useState<DrawingElement[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add to history when elements change
  const addToHistory = useCallback((newElements: DrawingElement[]) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newElements);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex((prev) => prev - 1);
      setElements(history[historyIndex - 1]);
      toast.success('Rückgängig');
    }
  }, [historyIndex, history]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex((prev) => prev + 1);
      setElements(history[historyIndex + 1]);
      toast.success('Wiederholen');
    }
  }, [historyIndex, history]);

  // Generate next L-number for dimension attributes
  const getNextLNumber = useCallback((): string => {
    const dimensionAttrs = attributes.filter(attr => attr.type === 'dimension' && attr.name.startsWith('L'));
    const numbers = dimensionAttrs
      .map(attr => {
        const match = attr.name.match(/^L(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
    return `L${maxNumber + 1}`;
  }, [attributes]);

  // Generate next W-number for angle attributes
  const getNextWNumber = useCallback((): string => {
    const angleAttrs = attributes.filter(attr => attr.type === 'angle' && attr.name.startsWith('W'));
    const numbers = angleAttrs
      .map(attr => {
        const match = attr.name.match(/^W(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
    return `W${maxNumber + 1}`;
  }, [attributes]);

  // Helper: Update angle dimensions when source lines change
  const updateAngleDimensionsForLine = useCallback((
    lineId: string, 
    updatedElements: DrawingElement[]
  ): { elements: DrawingElement[], attributeUpdates: Array<{ id: string, value: number }> } => {
    const attributeUpdates: Array<{ id: string, value: number }> = [];
    
    const newElements = updatedElements.map(el => {
      if (el.type === 'angle-dimension' && el.sourceElementIds?.includes(lineId)) {
        // Find both source lines
        const line1 = updatedElements.find(e => e.id === el.sourceElementIds![0] && e.type === 'line') as Line | undefined;
        const line2 = updatedElements.find(e => e.id === el.sourceElementIds![1] && e.type === 'line') as Line | undefined;
        
        if (!line1 || !line2) {
          // Source lines not found - keep existing angle dimension unchanged
          return el;
        }
        
        // Recalculate vertex and points
        const vertex = findCommonEndpoint(line1, line2);
        
        if (!vertex) {
          // Lines no longer share a common endpoint - keep existing angle dimension unchanged
          // This can happen when lines are moved independently
          return el;
        }
        
        const point1 = getOtherEndpoint(line1, vertex);
        const point2 = getOtherEndpoint(line2, vertex);
        
        // Recalculate angle value
        const angleValue = calculateAngle(vertex, point1, point2);
        
        // Recalculate optimal arc radius
        const arcRadius = calculateOptimalArcRadius(vertex, point1, point2);
        
        // Track attribute update
        if (el.attributeId) {
          attributeUpdates.push({
            id: el.attributeId,
            value: parseFloat(angleValue.toFixed(1))
          });
        }
        
        // Return updated angle dimension
        return {
          ...el,
          vertex,
          point1,
          point2,
          value: angleValue,
          arcRadius,
        } as AngleDimension;
      }
      return el;
    });
    
    return { elements: newElements, attributeUpdates };
  }, []);

  // Helper: Find line element at or near a point
  const findLineAtPoint = useCallback((point: IsoPoint, tolerancePixels = 15): Line | null => {
    // Check all line elements
    const lines = elements.filter(el => el.type === 'line') as Line[];
    
    // Convert click point to screen space
    const gridSize = backgroundSettings.isometricGridSize;
    const clickWorldPos = isoToScreen(point, gridSize);
    const clickScreenPos = worldToScreen(clickWorldPos, viewState);
    
    for (const line of lines) {
      // Convert line endpoints to screen space
      const startWorld = isoToScreen(line.start, gridSize);
      const endWorld = isoToScreen(line.end, gridSize);
      const startScreen = worldToScreen(startWorld, viewState);
      const endScreen = worldToScreen(endWorld, viewState);

      // Calculate distance from click point to line segment (in screen space)
      const A = clickScreenPos.x - startScreen.x;
      const B = clickScreenPos.y - startScreen.y;
      const C = endScreen.x - startScreen.x;
      const D = endScreen.y - startScreen.y;

      const dot = A * C + B * D;
      const lenSq = C * C + D * D;
      let param = -1;
      if (lenSq !== 0) param = dot / lenSq;

      let xx, yy;

      if (param < 0) {
        // Closest point is start of segment
        xx = startScreen.x;
        yy = startScreen.y;
      } else if (param > 1) {
        // Closest point is end of segment
        xx = endScreen.x;
        yy = endScreen.y;
      } else {
        // Closest point is on the segment
        xx = startScreen.x + param * C;
        yy = startScreen.y + param * D;
      }

      const dx = clickScreenPos.x - xx;
      const dy = clickScreenPos.y - yy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // If click is within tolerance, return this line
      if (dist <= tolerancePixels) {
        return line;
      }
    }
    
    return null;
  }, [elements, backgroundSettings, viewState]);

  // Handle cursor move for dynamic previews
  const handleCursorMove = useCallback((point: IsoPoint) => {
    if (currentTool === 'tangent-arc' && tangentArcStart) {
      const { sourceLine, isStartOfLine } = tangentArcStart;
      
      const gridSize = backgroundSettings.isometricGridSize;
      
      // Calculate screen coordinates
      const startWorld = isoToScreen(tangentArcStart.point, gridSize);
      const startScreen = worldToScreen(startWorld, viewState);
      
      // Get other end of source line to determine tangent direction
      const otherEndIso = isStartOfLine ? sourceLine.end : sourceLine.start;
      const otherEndWorld = isoToScreen(otherEndIso, gridSize);
      const otherEndScreen = worldToScreen(otherEndWorld, viewState);
      
      // Tangent vector (pointing OUT from the line endpoint)
      const dx = startScreen.x - otherEndScreen.x;
      const dy = startScreen.y - otherEndScreen.y;
      const len = Math.sqrt(dx*dx + dy*dy);
      
      if (len < 0.001) return;
      
      const tx = dx / len;
      const ty = dy / len;
      
      // Normal vector (perpendicular) - rotated 90 deg
      const nx = -ty;
      const ny = tx;
      
      // Current cursor position
      const cursorWorld = isoToScreen(point, gridSize);
      const cursorScreen = worldToScreen(cursorWorld, viewState);
      
      // Vector Start->Cursor
      const vx = cursorScreen.x - startScreen.x;
      const vy = cursorScreen.y - startScreen.y;
      
      // Project cursor onto normal vector to find center
      // P_proj = (V . N) * N
      const dotVN = vx * nx + vy * ny;
      
      // Center in screen coordinates
      // M = S + dotVN * N
      const cx = startScreen.x + dotVN * nx;
      const cy = startScreen.y + dotVN * ny;
      
      // Radius is the distance from Start to Center (which is absolute value of dotVN)
      const radiusScreen = Math.abs(dotVN);
      
      if (radiusScreen < 1) { // Avoid zero radius
        setPreviewElement(null); 
        return;
      }
      
      // Determine orientation (CW vs CCW)
      // dotVN > 0 means cursor is in direction of N -> CCW (since N is rotated -90 deg from T?)
      // Let's verify angles.
      // Angle M -> S
      const angleMtoS = Math.atan2(startScreen.y - cy, startScreen.x - cx);
      
      // For a 180 degree arc (semicircle), End Angle is Start Angle + PI (or - PI)
      // Direction depends on dotVN
      const isCCW = dotVN > 0;
      
      const startAngle = angleMtoS;
      const endAngle = startAngle + (isCCW ? -Math.PI : Math.PI); // Draw 180 degrees
      
      // Convert Center to IsoPoint
      const centerWorld = screenToWorld({x: cx, y: cy}, viewState);
      const centerIso = screenToIso(centerWorld, gridSize);
      
      // Construct Preview Element
      const newArc: Arc = {
        id: 'preview-arc',
        type: 'arc',
        center: centerIso,
        radius: radiusScreen / (gridSize * viewState.zoom),
        startAngle: startAngle,
        endAngle: endAngle,
        counterClockwise: !isCCW, // Note: Canvas arc direction is opposite to mathematical CCW sometimes, need to check visually.
        // Usually: false = Clockwise, true = CounterClockwise.
        // If we want M->S to be start, and draw 180 deg, we just need to sweep PI.
        color: sourceLine.color,
        width: sourceLine.width,
        lineStyle: sourceLine.lineStyle,
      };
      
      setPreviewElement(newArc);
    } else if (currentTool === 'centered-arc' && tempPoints.length === 1) {
      const centerIso = tempPoints[0];
      const apexIso = point;
      const gridSize = backgroundSettings.isometricGridSize;
      
      const centerWorld = isoToScreen(centerIso, gridSize);
      const centerScreen = worldToScreen(centerWorld, viewState);
      
      const apexWorld = isoToScreen(apexIso, gridSize);
      const apexScreen = worldToScreen(apexWorld, viewState);
      
      // Calculate radius in screen space
      const dx = apexScreen.x - centerScreen.x;
      const dy = apexScreen.y - centerScreen.y;
      const radiusScreen = Math.sqrt(dx * dx + dy * dy);
      
      if (radiusScreen < 1) {
        setPreviewElement(null);
        return;
      }
      
      const radius = radiusScreen / (gridSize * viewState.zoom);
      
      // Angle from Center to Apex (Axis of the arc)
      let axisAngle = Math.atan2(apexScreen.y - centerScreen.y, apexScreen.x - centerScreen.x);
      
      // Snap angle to 30-degree increments if snap is enabled
      if (snapEnabled) {
         const snapIncrement = Math.PI / 6; // 30 degrees
         axisAngle = Math.round(axisAngle / snapIncrement) * snapIncrement;
      }
      
      // Arc is a semicircle centered on the axis
      // We draw from Axis-90 to Axis+90
      const startAngle = axisAngle - Math.PI / 2;
      const endAngle = axisAngle + Math.PI / 2;
      
      const newArc: Arc = {
        id: 'preview-centered-arc',
        type: 'arc',
        center: centerIso,
        radius: radius,
        startAngle: startAngle,
        endAngle: endAngle,
        counterClockwise: false, // Clockwise in screen coords (Y down) is positive angle direction
        color: '#000000',
        width: 2,
        lineStyle: 'dashed',
      };
      
      // Lever (Visual Aid)
      const lever: Line = {
        id: 'preview-lever',
        type: 'line',
        start: centerIso,
        end: apexIso,
        color: '#ff0000', // Red
        width: 4,        // Thick
        lineStyle: 'solid'
      };
      
      setPreviewElement([newArc, lever]);
    } else {
      setPreviewElement(null);
    }
  }, [currentTool, tangentArcStart, backgroundSettings, viewState, tempPoints]);

  // Handle point addition based on current tool
  const handlePointAdd = useCallback(
    (point: IsoPoint) => {
      if (currentTool === 'line') {
        if (tempPoints.length === 0) {
          setTempPoints([point]);
        } else {
          // Create line
          const newLine: Line = {
            id: `line-${Date.now()}`,
            type: 'line',
            start: tempPoints[0],
            end: point,
            color: '#000000',
            width: 5,
          };
          
          let newElements = [...elements, newLine];
          
          // Auto-create dimension if enabled
          if (autoCreateDimension) {
            // Calculate distance in grid units
            const dx = point.u - tempPoints[0].u;
            const dy = point.v - tempPoints[0].v;
            const distanceInGridUnits = Math.sqrt(dx * dx + dy * dy);
            
            // Get current grid size in mm based on background mode
            const currentGridSizeMm = (() => {
              switch (backgroundSettings.mode) {
                case 'isometric':
                  return backgroundSettings.isometricGridSize;
                case 'cartesian':
                  return backgroundSettings.cartesianGridSize;
                case 'dots':
                  return backgroundSettings.dotsGridSize;
                default:
                  return 200; // Fallback
              }
            })();
            
            // Convert to mm: grid units * mm per grid unit
            const distanceInMm = distanceInGridUnits * currentGridSizeMm;
            
            // Generate attribute for this dimension
            const attrName = getNextLNumber();
            const newAttribute: PartAttribute = {
              id: `attr-${Date.now()}`,
              name: attrName,
              type: 'dimension',
              value: parseFloat(distanceInMm.toFixed(1)),
              originalName: attrName, // Store original auto-generated name
              readonly: true,
            };
            
            // Create dimension
            const newDim: Dimension = {
              id: `dim-${Date.now() + 1}`,
              type: 'dimension',
              start: tempPoints[0],
              end: point,
              value: distanceInMm.toFixed(1),
              unit: 'mm',
              showUnit: false,
              decimalPlaces: 0,
              offset: 0.5,
              color: '#000000',
              textSize: 50,
              lineWidth: 2.5,
              showLine: false,
              showArrows: false,
              arrowSize: 8,
              arrowFilled: false,
              arrowPosition: 'inside',
              attributeId: newAttribute.id,
            };
            
            // Link attribute to dimension
            newAttribute.dimensionId = newDim.id;
            
            newElements.push(newDim);
            setAttributes(prev => [...prev, newAttribute]);
            toast.success(`Linie mit Bemaßung "${attrName}" erstellt`);
          } else {
            toast.success('Linie erstellt');
          }
          
          setElements(newElements);
          addToHistory(newElements);
          setTempPoints([]);
        }
      } else if (currentTool === 'shape') {
        const newTempPoints = [...tempPoints, point];
        
        // Check if clicking NEAR first point to close shape (with tolerance)
        let shouldCloseShape = false;
        if (newTempPoints.length > 2) {
          const firstPoint = newTempPoints[0];
          
          // Check exact match first (when snap is enabled)
          if (isoPointsEqual(firstPoint, point)) {
            shouldCloseShape = true;
          } else {
            // Check distance in screen space for tolerance-based closing (when snap is disabled)
            const gridSize = backgroundSettings.isometricGridSize;
            const firstPointWorld = isoToScreen(firstPoint, gridSize);
            const clickPointWorld = isoToScreen(point, gridSize);
            const firstPointScreen = worldToScreen(firstPointWorld, viewState);
            const clickPointScreen = worldToScreen(clickPointWorld, viewState);
            
            const distancePixels = Math.sqrt(
              Math.pow(firstPointScreen.x - clickPointScreen.x, 2) +
              Math.pow(firstPointScreen.y - clickPointScreen.y, 2)
            );
            
            // Close shape if click is within 15 pixels of first point
            const closeTolerance = 15; // pixels
            if (distancePixels < closeTolerance) {
              shouldCloseShape = true;
              console.log('🔵 Shape auto-close: clicked within', distancePixels.toFixed(1), 'px of start point');
            }
          }
        }
        
        if (shouldCloseShape) {
          const newShape: Shape = {
            id: `shape-${Date.now()}`,
            type: 'shape',
            points: tempPoints, // Don't include the duplicate closing point
            fillColor: '#cccccc',
            strokeColor: '#000000',
            strokeWidth: 2,
            filled: true,
          };
          const newElements = [...elements, newShape];
          setElements(newElements);
          addToHistory(newElements);
          setTempPoints([]);
          toast.success('Fläche erstellt');
        } else {
          setTempPoints(newTempPoints);
        }
      } else if (currentTool === 'circle') {
        if (tempPoints.length === 0) {
          // First point: center
          setTempPoints([point]);
          toast.info('Klicke für den Radius');
        } else {
          // Second point: radius - calculate in screen space for accuracy
          const center = tempPoints[0];
          const gridSize = backgroundSettings.isometricGridSize;
          const centerWorldPos = isoToScreen(center, gridSize);
          const centerScreenPos = worldToScreen(centerWorldPos, viewState);
          
          const pointWorldPos = isoToScreen(point, gridSize);
          const pointScreenPos = worldToScreen(pointWorldPos, viewState);
          
          // Calculate radius in screen space, then convert back to isometric units
          const dx = pointScreenPos.x - centerScreenPos.x;
          const dy = pointScreenPos.y - centerScreenPos.y;
          const screenRadius = Math.sqrt(dx * dx + dy * dy);
          const radius = screenRadius / (gridSize * viewState.zoom);
          
          const newCircle: Circle = {
            id: `circle-${Date.now()}`,
            type: 'circle',
            center: center,
            radius: radius,
            color: '#000000',
            width: 2,
            filled: false,
            fillColor: '#cccccc',
          };
          const newElements = [...elements, newCircle];
          setElements(newElements);
          addToHistory(newElements);
          setTempPoints([]);
          toast.success('Kreis erstellt');
        }
      } else if (currentTool === 'arc') {
        if (tempPoints.length === 0) {
          // First point: center
          setTempPoints([point]);
          toast.info('Klicke für den Startpunkt');
        } else if (tempPoints.length === 1) {
          // Second point: start angle and radius
          setTempPoints([...tempPoints, point]);
          toast.info('Klicke für den Endpunkt');
        } else {
          // Third point: end angle
          const center = tempPoints[0];
          const startPoint = tempPoints[1];
          const endPoint = point;
          
          // Calculate radius and angles in screen space for accurate arc
          const gridSize = backgroundSettings.isometricGridSize;
          const centerWorldPos = isoToScreen(center, gridSize);
          const centerScreenPos = worldToScreen(centerWorldPos, viewState);
          
          const startWorldPos = isoToScreen(startPoint, gridSize);
          const startScreenPos = worldToScreen(startWorldPos, viewState);
          
          const endWorldPos = isoToScreen(endPoint, gridSize);
          const endScreenPos = worldToScreen(endWorldPos, viewState);
          
          // Calculate radius in screen space, then convert to isometric units
          const dx = startScreenPos.x - centerScreenPos.x;
          const dy = startScreenPos.y - centerScreenPos.y;
          const screenRadius = Math.sqrt(dx * dx + dy * dy);
          const radius = screenRadius / (gridSize * viewState.zoom);
          
          // Calculate angles in screen space
          const startAngle = Math.atan2(dy, dx);
          const endAngle = Math.atan2(endScreenPos.y - centerScreenPos.y, endScreenPos.x - centerScreenPos.x);
          
          const newArc: Arc = {
            id: `arc-${Date.now()}`,
            type: 'arc',
            center: center,
            radius: radius,
            startAngle: startAngle,
            endAngle: endAngle,
            color: '#000000',
            width: 2,
          };
          const newElements = [...elements, newArc];
          setElements(newElements);
          addToHistory(newElements);
          setTempPoints([]);
          toast.success('Bogen erstellt');
        }
      } else if (currentTool === 'centered-arc') {
        if (tempPoints.length === 0) {
          // First point: Center of Curvature
          setTempPoints([point]);
          toast.info('Klicke für den Scheitelpunkt (Magenta)');
        } else {
          // Second point: Apex (top of the hat)
          const center = tempPoints[0];
          const apex = point;
          
          const gridSize = backgroundSettings.isometricGridSize;
          const centerWorld = isoToScreen(center, gridSize);
          const centerScreen = worldToScreen(centerWorld, viewState);
          
          const apexWorld = isoToScreen(apex, gridSize);
          const apexScreen = worldToScreen(apexWorld, viewState);
          
          // Calculate radius
          const dx = apexScreen.x - centerScreen.x;
          const dy = apexScreen.y - centerScreen.y;
          const radiusScreen = Math.sqrt(dx * dx + dy * dy);
          const radius = radiusScreen / (gridSize * viewState.zoom);
          
          // Angle from Center to Apex (Axis of the arc)
          let axisAngle = Math.atan2(apexScreen.y - centerScreen.y, apexScreen.x - centerScreen.x);
          
          // Snap angle to 30-degree increments if snap is enabled
          if (snapEnabled) {
             const snapIncrement = Math.PI / 6; // 30 degrees
             axisAngle = Math.round(axisAngle / snapIncrement) * snapIncrement;
          }
          
          const startAngle = axisAngle - Math.PI / 2;
          const endAngle = axisAngle + Math.PI / 2;
          
          const newArc: Arc = {
            id: `centered-arc-${Date.now()}`,
            type: 'arc',
            center: center,
            radius: radius,
            startAngle: startAngle,
            endAngle: endAngle,
            counterClockwise: false,
            color: '#000000',
            width: 2,
          };
          
          const newElements = [...elements, newArc];
          setElements(newElements);
          addToHistory(newElements);
          setTempPoints([]);
          toast.success('Hut-Bogen erstellt');
        }
      } else if (currentTool === 'spline') {
        const newTempPoints = [...tempPoints, point];
        
        // Check if clicking on first point to close spline (requires at least 3 points)
        if (newTempPoints.length > 2 && isoPointsEqual(newTempPoints[0], point)) {
          const newSpline: Spline = {
            id: `spline-${Date.now()}`,
            type: 'spline',
            points: tempPoints, // Don't include the duplicate closing point
            color: '#000000',
            width: 2,
            tension: 0.5,
          };
          const newElements = [...elements, newSpline];
          setElements(newElements);
          addToHistory(newElements);
          setTempPoints([]);
          toast.success('Spline erstellt');
        } else if (newTempPoints.length >= 2) {
          setTempPoints(newTempPoints);
          toast.info('Klicke für weitere Punkte (ESC oder Doppelklick zum Beenden)');
        } else {
          setTempPoints(newTempPoints);
        }
      } else if (currentTool === 'text') {
        // Open text dialog
        setTempPoints([point]);
      } else if (currentTool === 'connection') {
        // Open dialog with temp connection
        setTempConnection({
          position: point,
          label: '',
        });
        setIsConnectionDialogOpen(true);
      } else if (currentTool === 'dimension') {
        if (tempPoints.length === 0) {
          setTempPoints([point]);
        } else {
          // Calculate distance in grid units
          const dx = point.u - tempPoints[0].u;
          const dy = point.v - tempPoints[0].v;
          const distanceInGridUnits = Math.sqrt(dx * dx + dy * dy);
          
          // Get current grid size in mm based on background mode
          const currentGridSizeMm = (() => {
            switch (backgroundSettings.mode) {
              case 'isometric':
                return backgroundSettings.isometricGridSize;
              case 'cartesian':
                return backgroundSettings.cartesianGridSize;
              case 'dots':
                return backgroundSettings.dotsGridSize;
              default:
                return 200; // Fallback
            }
          })();
          
          // Convert to mm: grid units * mm per grid unit
          const distanceInMm = distanceInGridUnits * currentGridSizeMm;
          
          // Generate attribute for this dimension
          const attrName = getNextLNumber();
          const newAttribute: PartAttribute = {
            id: `attr-${Date.now()}`,
            name: attrName,
            type: 'dimension',
            value: parseFloat(distanceInMm.toFixed(1)),
            originalName: attrName, // Store original auto-generated name
            readonly: true,
          };
          
          // Create dimension
          const newDimension: Dimension = {
            id: `dimension-${Date.now()}`,
            type: 'dimension',
            start: tempPoints[0],
            end: point,
            value: distanceInMm.toFixed(1),
            unit: 'mm',
            showUnit: false,
            decimalPlaces: 0,
            offset: 0.5,
            color: '#000000',
            textSize: 50,
            lineWidth: 2.5,
            showLine: false,
            showArrows: false,
            arrowSize: 8,
            arrowFilled: false,
            arrowPosition: 'inside',
            attributeId: newAttribute.id,
          };
          
          // Link attribute to dimension
          newAttribute.dimensionId = newDimension.id;
          
          const newElements = [...elements, newDimension];
          setElements(newElements);
          addToHistory(newElements);
          setAttributes(prev => [...prev, newAttribute]);
          setTempPoints([]);
          toast.success(`Bemaßung "${attrName}" erstellt`);
        }
      } else if (currentTool === 'angle-dimension') {
        // Smart angle tool: Click on two lines to measure angle between them
        const clickedLine = findLineAtPoint(point);
        
        if (!clickedLine) {
          toast.error('Keine Linie gefunden. Klicke auf eine Linie.');
          return;
        }
        
        if (selectedLines.length === 0) {
          // First line selected
          setSelectedLines([clickedLine.id]);
          toast.info('Erste Linie ausgewählt. Klicke auf zweite Linie.');
        } else if (selectedLines.length === 1) {
          // Second line selected - create angle dimension
          const firstLine = elements.find(el => el.id === selectedLines[0] && el.type === 'line') as Line;
          
          if (!firstLine) {
            setSelectedLines([]);
            toast.error('Erste Linie nicht gefunden');
            return;
          }
          
          // Find common endpoint (vertex)
          const vertex = findCommonEndpoint(firstLine, clickedLine);
          
          if (!vertex) {
            toast.error('Linien teilen keinen gemeinsamen Endpunkt');
            setSelectedLines([]);
            return;
          }
          
          // Get the other endpoints
          const point1 = getOtherEndpoint(firstLine, vertex);
          const point2 = getOtherEndpoint(clickedLine, vertex);
          
          // Calculate angle value
          const angleValue = calculateAngle(vertex, point1, point2);
          
          // Calculate optimal arc radius
          const arcRadius = calculateOptimalArcRadius(vertex, point1, point2);
          
          // Generate attribute for this angle dimension
          const attrName = getNextWNumber();
          const newAttribute: PartAttribute = {
            id: `attr-${Date.now()}`,
            name: attrName,
            type: 'angle',
            value: parseFloat(angleValue.toFixed(1)),
            originalName: attrName, // Store original auto-generated name
            readonly: true,
          };
          
          // Create angle dimension
          const newAngleDimension: AngleDimension = {
            id: `angle-dim-${Date.now()}`,
            type: 'angle-dimension',
            vertex: vertex,
            point1: point1,
            point2: point2,
            value: angleValue,
            unit: '°',
            showUnit: true,
            decimalPlaces: 1,
            arcRadius: arcRadius,
            color: '#000000',
            lineWidth: 2,
            textSize: 50,
            showArc: true,
            showArms: false,
            sourceElementIds: [firstLine.id, clickedLine.id],
            attributeId: newAttribute.id,
          };
          
          // Link attribute to angle dimension
          newAttribute.angleDimensionId = newAngleDimension.id;
          
          const newElements = [...elements, newAngleDimension];
          setElements(newElements);
          addToHistory(newElements);
          setAttributes(prev => [...prev, newAttribute]);
          setSelectedLines([]);
          setAngleDimensionPreview(null);
          toast.success(`Winkel "${attrName}" ${angleValue.toFixed(1)}° erstellt`);
        }
      } else if (currentTool === 'tangent-arc') {
        if (!tangentArcStart) {
          // First click: select line endpoint
          const line = findLineAtPoint(point);
          if (line) {
             // Determine which end is closer
             const gridSize = backgroundSettings.isometricGridSize;
             const clickWorld = isoToScreen(point, gridSize);
             const clickScreen = worldToScreen(clickWorld, viewState);
             
             const startWorld = isoToScreen(line.start, gridSize);
             const startScreen = worldToScreen(startWorld, viewState);
             const endWorld = isoToScreen(line.end, gridSize);
             const endScreen = worldToScreen(endWorld, viewState);
             
             const distStart = Math.pow(clickScreen.x - startScreen.x, 2) + Math.pow(clickScreen.y - startScreen.y, 2);
             const distEnd = Math.pow(clickScreen.x - endScreen.x, 2) + Math.pow(clickScreen.y - endScreen.y, 2);
             
             const isStart = distStart < distEnd;
             const anchorPoint = isStart ? line.start : line.end;
             
             setTangentArcStart({
               point: anchorPoint,
               sourceLine: line,
               isStartOfLine: isStart
             });
             toast.info('Bewege die Maus um den Bogen zu formen, Klick zum Bestätigen');
          } else {
             toast.error('Bitte klicke auf ein Linienende');
          }
        } else {
           // Second click: finish arc
           if (previewElement && previewElement.type === 'arc') {
              const finalArc = { ...previewElement, id: `arc-${Date.now()}` };
              const newElements = [...elements, finalArc];
              setElements(newElements);
              addToHistory(newElements);
              setTangentArcStart(null);
              setPreviewElement(null);
              toast.success('Tangentialbogen erstellt');
           } else {
              setTangentArcStart(null);
              setPreviewElement(null);
              toast.info('Abgebrochen: Kein gültiger Bogen');
           }
        }
      }
    },
    [currentTool, tempPoints, elements, addToHistory, viewState, backgroundSettings, getNextLNumber, getNextWNumber, attributes, findLineAtPoint, selectedLines, tangentArcStart, previewElement, autoCreateDimension]
  );

  // Handle connection dialog confirm
  const handleConnectionConfirm = useCallback(() => {
    if (!tempConnection) return;

    const newConnection: ConnectionPoint = {
      id: `connection-${Date.now()}`,
      type: 'connection',
      position: tempConnection.position,
      label: tempConnection.label,
      diameter: 50, // 50mm default diameter
      color: '#000000', // black default
      fillType: 'ring' as const, // ring default (like in the image)
      strokeWidth: 5, // 5px default stroke width
    };
    const newElements = [...elements, newConnection];
    setElements(newElements);
    addToHistory(newElements);
    setIsConnectionDialogOpen(false);
    setTempConnection(null);
    toast.success('Verbindungspunkt erstellt');
  }, [tempConnection, elements, addToHistory]);

  // Handle connection dialog cancel
  const handleConnectionCancel = useCallback(() => {
    setIsConnectionDialogOpen(false);
    setTempConnection(null);
  }, []);

  // Handle text dialog confirm
  const handleTextConfirm = useCallback((text: string, rotation: number) => {
    if (tempPoints.length === 0) return;

    const newText: TextElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      position: tempPoints[0],
      text: text,
      fontSize: 50,
      color: '#000000',
      rotation: rotation,
    };
    const newElements = [...elements, newText];
    setElements(newElements);
    addToHistory(newElements);
    setTempPoints([]);
    toast.success('Text erstellt');
  }, [tempPoints, elements, addToHistory]);

  // Handle text dialog cancel
  const handleTextCancel = useCallback(() => {
    setTempPoints([]);
  }, []);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.min(10, prev.zoom * 1.2),
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(0.1, prev.zoom / 1.2),
    }));
  }, []);

  const handleZoomReset = useCallback(() => {
    setViewState({
      zoom: 1,
      panX: 400,
      panY: 300,
    });
  }, []);

  // Helper to generate part data
  const getPartData = useCallback((filename: string) => {
    const now = new Date().toISOString();
    const currentDate = partCreatedDate || now;
    
    // Add changelog entry for this save
    const newChangelogEntry: ChangelogEntry = {
      timestamp: now,
      editor: partAuthor || undefined,
      description: t('status.saved'),
    };
    
    // Prepare changelog
    const updatedChangelog = [...partChangelog, newChangelogEntry];

    const isomPart: IsomPart = {
      name: filename,
      version: '1.0',
      created: currentDate,
      modified: now,
      elements: elements,
      attributes: attributes,
      backgroundSettings: backgroundSettings,
      changelog: updatedChangelog,
      author: partAuthor || undefined,
      lastModifiedBy: partAuthor || undefined,
    };

    const blob = new Blob([JSON.stringify(isomPart, null, 2)], {
      type: 'application/json',
    });
    
    return { blob, newChangelogEntry, currentDate };
  }, [elements, attributes, backgroundSettings, partChangelog, partCreatedDate, partAuthor]);

  // Save As - always ask for new filename
  const handleSaveAs = useCallback(async () => {
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `${currentPartName}.isompart`,
          types: [{
            description: 'IsoCAD Bauteil',
            accept: { 'application/json': ['.isompart'] }
          }],
        });
        
        const name = handle.name.replace(/\.isompart$/i, '');
        setCurrentPartName(name);
        
        const { blob, newChangelogEntry, currentDate } = getPartData(name);
        
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        // Update state
        setPartChangelog([...partChangelog, newChangelogEntry]);
        if (!partCreatedDate) setPartCreatedDate(currentDate);
        
        toast.success(t('status.saved'));
      } catch (err: any) {
         if (err.name !== 'AbortError') {
             console.error(err);
             toast.error('Fehler beim Speichern');
         }
      }
    } else {
        // Fallback: Trigger download directly to allow browser's "Save As" dialog (if configured)
        const filename = currentPartName;
        const { blob, newChangelogEntry, currentDate } = getPartData(filename);
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename.replace(/\s+/g, '_')}.isompart`;
        a.click();
        URL.revokeObjectURL(url);
        
        setPartChangelog([...partChangelog, newChangelogEntry]);
        if (!partCreatedDate) setPartCreatedDate(currentDate);
        
        toast.success(`Bauteil exportiert (Browser-Download)`);
    }
  }, [currentPartName, getPartData, partChangelog, partCreatedDate]);

  // Save to file
  const handleSave = useCallback(async () => {
    if (currentPartName === 'Neues Bauteil') {
      await handleSaveAs();
      return;
    }
    
    // Quick save (download)
    const { blob, newChangelogEntry, currentDate } = getPartData(currentPartName);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPartName.replace(/\s+/g, '_')}.isompart`;
    a.click();
    URL.revokeObjectURL(url);
    
    setPartChangelog([...partChangelog, newChangelogEntry]);
    if (!partCreatedDate) setPartCreatedDate(currentDate);
    
    toast.success(t('status.saved'));
  }, [currentPartName, handleSaveAs, getPartData, partChangelog, partCreatedDate]);

  // Load from file
  const handleLoad = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const isomPart: IsomPart = JSON.parse(event.target?.result as string);
        
        // Restore all data
        setElements(isomPart.elements);
        setCurrentPartName(isomPart.name);
        setAttributes(isomPart.attributes || []);
        
        // Restore metadata
        setPartCreatedDate(isomPart.created);
        setPartAuthor(isomPart.author || null);
        setPartChangelog(isomPart.changelog || []);
        
        // Restore background settings if available
        if (isomPart.backgroundSettings) {
          setBackgroundSettings(isomPart.backgroundSettings);
        }
        
        addToHistory(isomPart.elements);
        setSelectedIds([]);
        setTempPoints([]);
        
        // Show detailed info in console
        console.log('Bauteil geladen:', {
          name: isomPart.name,
          created: isomPart.created,
          modified: isomPart.modified,
          author: isomPart.author,
          elements: isomPart.elements.length,
          attributes: (isomPart.attributes || []).length,
          changelogEntries: (isomPart.changelog || []).length,
        });
        
        toast.success(`Bauteil \"${isomPart.name}\" geladen`);
      } catch (error) {
        toast.error('Fehler beim Laden der Datei');
        console.error(error);
      }
    };
    reader.readAsText(file);

    // Reset input
    e.target.value = '';
  }, [addToHistory]);

  // Load part from library
  const handleLoadFromLibrary = useCallback(
    (loadedElements: DrawingElement[], name: string) => {
      setElements(loadedElements);
      setCurrentPartName(name);
      addToHistory(loadedElements);
      setSelectedIds([]);
      setTempPoints([]);
    },
    [addToHistory]
  );

  // Clear canvas
  const handleClear = useCallback(() => {
    if (elements.length === 0) return;
    
    if (window.confirm('Möchten Sie wirklich alle Elemente löschen?')) {
      const newElements: DrawingElement[] = [];
      setElements(newElements);
      addToHistory(newElements);
      setSelectedIds([]);
      setTempPoints([]);
      toast.success('Alle Elemente gelöscht');
    }
  }, [elements.length, addToHistory]);

  // New part
  const handleNewPart = useCallback(() => {
    if (elements.length > 0 && !window.confirm('Aktuelles Bauteil verwerfen?')) {
      return;
    }
    
    const name = prompt('Name des neuen Bauteils:', 'Neues Bauteil');
    if (name) {
      setCurrentPartName(name);
      const newElements: DrawingElement[] = [];
      setElements(newElements);
      setHistory([[]]);
      setHistoryIndex(0);
      setSelectedIds([]);
      setTempPoints([]);
      toast.success(`Neues Bauteil "${name}" erstellt`);
    }
  }, [elements.length]);

  // Update element properties (without history, for live updates during dragging)
  const handleElementUpdate = useCallback(
    (id: string, updates: Partial<DrawingElement>) => {
      let newElements = elements.map((el) =>
        el.id === id ? { ...el, ...updates } : el
      );
      
      const updatedElement = newElements.find(el => el.id === id);

      // Check if the updated element is a dimension - if so, update value based on geometry
      if (updatedElement?.type === 'dimension') {
        const dim = updatedElement as Dimension;
        // Only update value if geometry changed and value wasn't explicitly updated
        // However, updates contains the partial update.
        // If 'start' or 'end' is in updates, we should recalculate value.
        // We only want to auto-calculate if the user is dragging (which calls this function with geometry updates)
        if ('start' in updates || 'end' in updates) {
          const du = dim.end.u - dim.start.u;
          const dv = dim.end.v - dim.start.v;
          const distanceInGridUnits = Math.sqrt(du * du + dv * dv);
          
          // Get current grid size in mm
          const currentGridSizeMm = (() => {
            switch (backgroundSettings.mode) {
              case 'isometric': return backgroundSettings.isometricGridSize;
              case 'cartesian': return backgroundSettings.cartesianGridSize;
              case 'dots': return backgroundSettings.dotsGridSize;
              default: return 200;
            }
          })();
          
          const distanceInMm = distanceInGridUnits * currentGridSizeMm;
          dim.value = distanceInMm.toFixed(dim.decimalPlaces ?? 0);

          // Also update linked attribute if exists
          if (dim.attributeId) {
             const attr = attributes.find(a => a.id === dim.attributeId);
             if (attr) {
               // Update attribute value separately
               // We can't update attributes state here easily without causing re-renders or complexity
               // but we can try updating the local attributes state later or let the complete handler do it
             }
          }
        }
      }

      // Check if the updated element is a line - if so, update angle dimensions
      if (updatedElement?.type === 'line') {
        const { elements: updatedWithAngles, attributeUpdates } = updateAngleDimensionsForLine(id, newElements);
        newElements = updatedWithAngles;
        
        // Update attributes in real-time
        if (attributeUpdates.length > 0) {
          setAttributes(prev => prev.map(attr => {
            const update = attributeUpdates.find(u => u.id === attr.id);
            return update ? { ...attr, value: update.value } : attr;
          }));
        }
      }
      
      setElements(newElements);
    },
    [elements, updateAngleDimensionsForLine, backgroundSettings, attributes]
  );

  // Update element and add to history (for completed edits)
  const handleElementUpdateComplete = useCallback(
    (id: string, updates: Partial<DrawingElement>) => {
      let newElements = elements.map((el) =>
        el.id === id ? { ...el, ...updates } : el
      );

      const updatedElement = newElements.find(el => el.id === id);
      
      // Check if the updated element is a dimension - if so, update value based on geometry
      if (updatedElement?.type === 'dimension') {
        const dim = updatedElement as Dimension;
        if ('start' in updates || 'end' in updates) {
          const du = dim.end.u - dim.start.u;
          const dv = dim.end.v - dim.start.v;
          const distanceInGridUnits = Math.sqrt(du * du + dv * dv);
          
          const currentGridSizeMm = (() => {
            switch (backgroundSettings.mode) {
              case 'isometric': return backgroundSettings.isometricGridSize;
              case 'cartesian': return backgroundSettings.cartesianGridSize;
              case 'dots': return backgroundSettings.dotsGridSize;
              default: return 200;
            }
          })();
          
          const distanceInMm = distanceInGridUnits * currentGridSizeMm;
          const newValue = distanceInMm.toFixed(dim.decimalPlaces ?? 0);
          dim.value = newValue;
          
          // Update linked attribute
          if (dim.attributeId) {
            setAttributes(prev => prev.map(attr => 
              attr.id === dim.attributeId ? { ...attr, value: parseFloat(newValue) } : attr
            ));
          }
        }
      }

      // Check if the updated element is a line - if so, update angle dimensions
      if (updatedElement?.type === 'line') {
        const { elements: updatedWithAngles, attributeUpdates } = updateAngleDimensionsForLine(id, newElements);
        newElements = updatedWithAngles;
        
        // Update attributes
        if (attributeUpdates.length > 0) {
          setAttributes(prev => prev.map(attr => {
            const update = attributeUpdates.find(u => u.id === attr.id);
            return update ? { ...attr, value: update.value } : attr;
          }));
        }
      }
      
      setElements(newElements);
      addToHistory(newElements);
    },
    [elements, addToHistory, updateAngleDimensionsForLine, backgroundSettings]
  );

  // Duplicate selected elements
  const handleDuplicate = useCallback(() => {
    if (selectedIds.length === 0) return;

    const duplicated = elements
      .filter((el) => selectedIds.includes(el.id))
      .map((el) => {
        const newId = `${el.type}-${Date.now()}-${Math.random()}`;
        if (el.type === 'line') {
          return {
            ...el,
            id: newId,
            start: { u: el.start.u + 2, v: el.start.v + 2 },
            end: { u: el.end.u + 2, v: el.end.v + 2 },
          };
        } else if (el.type === 'shape') {
          return {
            ...el,
            id: newId,
            points: el.points.map((p) => ({ u: p.u + 2, v: p.v + 2 })),
          };
        } else if (el.type === 'text') {
          return {
            ...el,
            id: newId,
            position: { u: el.position.u + 2, v: el.position.v + 2 },
          };
        } else if (el.type === 'circle') {
          return {
            ...el,
            id: newId,
            center: { u: el.center.u + 2, v: el.center.v + 2 },
          };
        } else if (el.type === 'arc') {
          return {
            ...el,
            id: newId,
            center: { u: el.center.u + 2, v: el.center.v + 2 },
          };
        } else if (el.type === 'spline') {
          return {
            ...el,
            id: newId,
            points: el.points.map((p) => ({ u: p.u + 2, v: p.v + 2 })),
          };
        } else if (el.type === 'connection') {
          return {
            ...el,
            id: newId,
            position: { u: el.position.u + 2, v: el.position.v + 2 },
          };
        } else if (el.type === 'dimension') {
          return {
            ...el,
            id: newId,
            start: { u: el.start.u + 2, v: el.start.v + 2 },
            end: { u: el.end.u + 2, v: el.end.v + 2 },
          };
        } else if (el.type === 'angle-dimension') {
          return {
            ...el,
            id: newId,
            vertex: { u: el.vertex.u + 2, v: el.vertex.v + 2 },
            point1: { u: el.point1.u + 2, v: el.point1.v + 2 },
            point2: { u: el.point2.u + 2, v: el.point2.v + 2 },
          };
        }
        return { ...el, id: newId };
      });

    const newElements = [...elements, ...duplicated];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedIds(duplicated.map((el) => el.id));
    toast.success('Elemente dupliziert');
  }, [selectedIds, elements, addToHistory]);

  // Delete selected elements
  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return;

    // Find attributes linked to dimensions or angle-dimensions that are being deleted
    const deletedElements = elements.filter((el) => selectedIds.includes(el.id));
    const attributeIdsToDelete: string[] = [];
    
    deletedElements.forEach(el => {
      if (el.type === 'dimension' && el.attributeId) {
        attributeIdsToDelete.push(el.attributeId);
      } else if (el.type === 'angle-dimension' && el.attributeId) {
        attributeIdsToDelete.push(el.attributeId);
      }
    });

    // Remove elements
    const newElements = elements.filter((el) => !selectedIds.includes(el.id));
    setElements(newElements);
    addToHistory(newElements);
    setSelectedIds([]);
    
    // Remove linked attributes
    if (attributeIdsToDelete.length > 0) {
      setAttributes(prev => prev.filter(attr => !attributeIdsToDelete.includes(attr.id)));
      toast.success(`${deletedElements.length} Element(e) und ${attributeIdsToDelete.length} verknüpfte(s) Attribut(e) gelöscht`);
    } else {
      toast.success('Elemente gelöscht');
    }
  }, [selectedIds, elements, addToHistory]);

  // Attribute management
  const handleAttributeAdd = useCallback((attribute: Omit<PartAttribute, 'id'>) => {
    const newAttribute: PartAttribute = {
      ...attribute,
      id: `attr-${Date.now()}`,
    };
    setAttributes(prev => [...prev, newAttribute]);
    toast.success(`Attribut "${newAttribute.name}" hinzugefügt`);
  }, []);

  const handleAttributeUpdate = useCallback((id: string, updates: Partial<PartAttribute>) => {
    setAttributes(prev => prev.map(attr => 
      attr.id === id ? { ...attr, ...updates } : attr
    ));
  }, []);

  const handleAttributeDelete = useCallback((id: string) => {
    const attr = attributes.find(a => a.id === id);
    setAttributes(prev => prev.filter(a => a.id !== id));
    
    // Remove link from dimension if exists
    if (attr?.dimensionId) {
      const newElements = elements.map(el => 
        el.type === 'dimension' && el.id === attr.dimensionId
          ? { ...el, attributeId: undefined }
          : el
      );
      setElements(newElements);
      addToHistory(newElements);
    }
    
    // Remove link from angle dimension if exists
    if (attr?.angleDimensionId) {
      const newElements = elements.map(el => 
        el.type === 'angle-dimension' && el.id === attr.angleDimensionId
          ? { ...el, attributeId: undefined }
          : el
      );
      setElements(newElements);
      addToHistory(newElements);
    }
    
    toast.success('Attribut gelöscht');
  }, [attributes, elements, addToHistory]);

  // Reset angle dimension state when changing tools
  useEffect(() => {
    if (currentTool !== 'angle-dimension') {
      setSelectedLines([]);
      setAngleDimensionPreview(null);
    }
  }, [currentTool]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore keyboard shortcuts when typing in input fields, textareas, or contenteditable elements
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Tool shortcuts
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'v':
            setCurrentTool('select');
            setTempPoints([]);
            break;
          case 'l':
            setCurrentTool('line');
            setTempPoints([]);
            break;
          case 'p':
            setCurrentTool('shape');
            setTempPoints([]);
            break;
          case 't':
            setCurrentTool('text');
            setTempPoints([]);
            break;
          case 'h':
            setCurrentTool('pan');
            setTempPoints([]);
            break;
          case 'c':
            setCurrentTool('circle');
            setTempPoints([]);
            break;
          case 's':
            setCurrentTool('spline');
            setTempPoints([]);
            break;
          case 'a':
            setCurrentTool('arc');
            setTempPoints([]);
            break;
          case 'k':
            setCurrentTool('connection');
            setTempPoints([]);
            break;
          case 'd':
            setCurrentTool('dimension');
            setTempPoints([]);
            break;
          case 'w':
            setCurrentTool('angle-dimension');
            setTempPoints([]);
            break;
          case 'g':
            setBackgroundSettings((prev) => ({ ...prev, visible: !prev.visible }));
            break;
          case '+':
          case '=':
            handleZoomIn();
            break;
          case '-':
            handleZoomOut();
            break;
          case '0':
            handleZoomReset();
            break;
          case 'escape':
            // Cancel current operation and return to select mode
            setTempPoints([]);
            setSelectedIds([]);
            setSelectedLines([]);
            setAngleDimensionPreview(null);
            setCurrentTool('select');
            break;
          case 'enter':
            // Finish spline with Enter key
            if (currentTool === 'spline' && tempPoints.length >= 2) {
              const newSpline: Spline = {
                id: `spline-${Date.now()}`,
                type: 'spline',
                points: tempPoints,
                color: '#000000',
                width: 2,
                tension: 0.5,
              };
              const newElements = [...elements, newSpline];
              setElements(newElements);
              addToHistory(newElements);
              setTempPoints([]);
              toast.success('Spline erstellt');
            }
            break;
          case 'delete':
          case 'backspace':
            handleDeleteSelected();
            break;
        }
      }

      // Ctrl/Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'c':
            e.preventDefault();
            if (selectedIds.length > 0) {
              const selectedElements = elements.filter((el) => selectedIds.includes(el.id));
              setClipboard(selectedElements);
              toast.success(`${selectedElements.length} ${selectedElements.length === 1 ? 'Element' : 'Elemente'} kopiert`);
            }
            break;
          case 'v':
            e.preventDefault();
            if (clipboard.length > 0) {
              const pastedElements = clipboard.map((el) => {
                const newId = `${el.type}-${Date.now()}-${Math.random()}`;
                if (el.type === 'line') {
                  return {
                    ...el,
                    id: newId,
                    start: { u: el.start.u + 1, v: el.start.v + 1 },
                    end: { u: el.end.u + 1, v: el.end.v + 1 },
                  };
                } else if (el.type === 'shape') {
                  return {
                    ...el,
                    id: newId,
                    points: el.points.map((p) => ({ u: p.u + 1, v: p.v + 1 })),
                  };
                } else if (el.type === 'text') {
                  return {
                    ...el,
                    id: newId,
                    position: { u: el.position.u + 1, v: el.position.v + 1 },
                  };
                } else if (el.type === 'circle') {
                  return {
                    ...el,
                    id: newId,
                    center: { u: el.center.u + 1, v: el.center.v + 1 },
                  };
                } else if (el.type === 'arc') {
                  return {
                    ...el,
                    id: newId,
                    center: { u: el.center.u + 1, v: el.center.v + 1 },
                  };
                } else if (el.type === 'spline') {
                  return {
                    ...el,
                    id: newId,
                    points: el.points.map((p) => ({ u: p.u + 1, v: p.v + 1 })),
                  };
                } else if (el.type === 'connection') {
                  return {
                    ...el,
                    id: newId,
                    position: { u: el.position.u + 1, v: el.position.v + 1 },
                  };
                } else if (el.type === 'dimension') {
                  return {
                    ...el,
                    id: newId,
                    start: { u: el.start.u + 1, v: el.start.v + 1 },
                    end: { u: el.end.u + 1, v: el.end.v + 1 },
                  };
                }
                return { ...el, id: newId };
              });

              const newElements = [...elements, ...pastedElements];
              setElements(newElements);
              addToHistory(newElements);
              setSelectedIds(pastedElements.map((el) => el.id));
              toast.success(`${pastedElements.length} ${pastedElements.length === 1 ? 'Element' : 'Elemente'} eingefügt`);
            }
            break;
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
          case 's':
            e.preventDefault();
            handleSave();
            break;
          case 'o':
            e.preventDefault();
            handleLoad();
            break;
          case 'n':
            e.preventDefault();
            handleNewPart();
            break;
          case 'b':
            e.preventDefault();
            setShowLibrary(true);
            break;
          case 'h':
            e.preventDefault();
            setShowHelp(true);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedIds,
    elements,
    currentTool,
    tempPoints,
    handleUndo,
    handleRedo,
    handleSave,
    handleLoad,
    handleNewPart,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleDeleteSelected,
    addToHistory,
    clipboard,
  ]);

  const handleElementAdd = useCallback((element: DrawingElement) => {
    const newElements = [...elements, element];
    setElements(newElements);
    addToHistory(newElements);
    toast.success('Element erstellt');
  }, [elements, addToHistory]);

  const handleBatchUpdate = useCallback((
    added: DrawingElement[],
    updated: { id: string, updates: Partial<DrawingElement> }[],
    deleted: string[]
  ) => {
    let newElements = [...elements];

    // Apply deletions
    if (deleted.length > 0) {
      newElements = newElements.filter(el => !deleted.includes(el.id));
    }

    // Apply updates
    if (updated.length > 0) {
      newElements = newElements.map(el => {
        const update = updated.find(u => u.id === el.id);
        if (update) {
          const updatedEl = { ...el, ...update.updates };
          
          // Logic for angle dimensions updates (from handleElementUpdate)
          if (updatedEl.type === 'line') {
             // We'll handle this post-loop or assume angle dimensions update in the next cycle/manually
             // For simplicity in batch, we just apply properties. 
             // Ideally we should recalculate dependent dimensions here too.
          }
          return updatedEl;
        }
        return el;
      });
      
      // Handle dependent updates (Angle dimensions) for all updated lines
      updated.forEach(update => {
         const el = newElements.find(e => e.id === update.id);
         if (el && el.type === 'line') {
             const { elements: updatedWithAngles, attributeUpdates } = updateAngleDimensionsForLine(el.id, newElements);
             newElements = updatedWithAngles;
             
             if (attributeUpdates.length > 0) {
                 setAttributes(prev => prev.map(attr => {
                    const attrUpdate = attributeUpdates.find(u => u.id === attr.id);
                    return attrUpdate ? { ...attr, value: attrUpdate.value } : attr;
                 }));
             }
         }
      });
    }

    // Apply additions
    if (added.length > 0) {
      newElements = [...newElements, ...added];
    }

    setElements(newElements);
    addToHistory(newElements);
    
    if (added.length > 0 || updated.length > 0 || deleted.length > 0) {
      toast.success('Änderungen angewendet');
    }
  }, [elements, addToHistory, updateAngleDimensionsForLine]);

  const selectedElements = elements.filter((el) => selectedIds.includes(el.id));

  // Calculate icon size based on multiplier (base size: 20px)
  const iconSize = Math.round(20 * (viewDisplaySettings.iconSizeMultiplier ?? 1.0));

  return (
    <div className="h-full flex flex-col overflow-hidden" data-module="iso-block">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".isompart"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Toolbar */}
      <Toolbar
        currentTool={currentTool}
        onToolChange={(tool) => {
          setCurrentTool(tool);
          setSelectedIds([]);
          setTempPoints([]);
        }}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onToggleDrawer={() => setDrawerOpen(true)}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        showGrid={backgroundSettings.visible}
        onToggleGrid={() => setBackgroundSettings((prev) => ({ ...prev, visible: !prev.visible }))}
        snapEnabled={snapEnabled}
        onToggleSnap={() => setSnapEnabled(!snapEnabled)}
        zoom={viewState.zoom}
        iconSize={iconSize}
        trimLines={trimFilletLines}
        onToggleTrimLines={() => setTrimFilletLines(!trimFilletLines)}
        showProperties={showProperties}
        onToggleProperties={() => setShowProperties(!showProperties)}
      />

      {/* Line Tool Options */}
      {currentTool === 'line' && (
        <div 
          className="px-4 py-2 flex items-center gap-2 border-b"
          style={{
            backgroundColor: 'var(--md-surface-container-low)',
            borderColor: 'var(--md-outline-variant)'
          }}
        >
           <div className="flex items-center gap-2">
              <Checkbox 
                id="auto-dimension" 
                checked={autoCreateDimension}
                onCheckedChange={(c) => setAutoCreateDimension(!!c)}
              />
              <label 
                htmlFor="auto-dimension" 
                className="text-sm font-medium leading-none cursor-pointer select-none"
                style={{ color: 'var(--md-on-surface)' }}
              >
                Automatisch Längenbemaßung erstellen
              </label>
           </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Canvas */}
        <div className="flex-1 bg-white">
          <IsometricCanvas
            elements={(() => {
              let displayElements = [...elements];
              
              // Add temp connection if present
              if (tempConnection) {
                displayElements.push({
                  id: 'temp-connection',
                  type: 'connection' as const,
                  position: tempConnection.position,
                  label: tempConnection.label,
                  diameter: 50,
                  color: '#000000',
                  fillType: 'ring' as const,
                  strokeWidth: 5,
                });
              }
              
              return displayElements;
            })()}
            selectedIds={selectedIds}
            currentTool={currentTool}
            viewState={viewState}
            tempPoints={tempPoints}
            onViewStateChange={setViewState}
            onPointAdd={handlePointAdd}
            onElementSelect={setSelectedIds}
            onElementUpdate={handleElementUpdate}
            onElementUpdateComplete={handleElementUpdateComplete}
            onElementAdd={handleElementAdd}
            onBatchUpdate={handleBatchUpdate}
            backgroundSettings={activeBackgroundSettings}
            attributes={attributes}
            textSnapEnabled={snapEnabled}
            trimLines={trimFilletLines}
            onCursorMove={handleCursorMove}
            previewElement={previewElement}
          />
        </div>

        {/* Properties panel wrapper */}
        <div 
          className={cn(
            "bg-gray-50 border-l border-gray-200 z-20 transition-all duration-300 ease-in-out flex flex-col",
            // Mobile: Absolute overlay
            "absolute right-0 top-0 bottom-0 shadow-xl lg:shadow-none",
            showProperties ? "translate-x-0" : "translate-x-full",
            // Desktop: Relative sliding drawer
            "lg:static lg:translate-x-0",
            showProperties ? "lg:w-72" : "lg:w-0 lg:border-none"
          )}
        >
          <div className="w-72 h-full overflow-hidden flex flex-col">
            <PropertiesPanel
              selectedElements={selectedElements}
              onElementUpdate={handleElementUpdateComplete}
              attributes={attributes}
              elements={elements}
              onAttributeAdd={handleAttributeAdd}
              onAttributeUpdate={handleAttributeUpdate}
              onAttributeDelete={handleAttributeDelete}
              gridSize={activeBackgroundSettings.isometricGridSize}
            />
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div 
        className="px-4 py-2 flex items-center justify-between text-xs"
        style={{
          backgroundColor: 'var(--md-surface-container-low)',
          color: 'var(--md-on-surface-variant)',
          borderTop: '1px solid var(--md-outline-variant)',
        }}
      >
        <div className="flex gap-4">
          <span>Elemente: {elements.length}</span>
          <span>Werkzeug: {currentTool}</span>
          {tempPoints.length > 0 && (
            <span style={{ color: 'var(--md-primary)' }}>
              Punkte: {tempPoints.length} (ESC zum Abbrechen)
            </span>
          )}
        </div>
        <div>
          Zoom: {Math.round(viewState.zoom * 100)}%
        </div>
      </div>

      {/* Parts Library Dialog */}
      <PartsLibrary
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        onLoadPart={handleLoadFromLibrary}
        currentElements={elements}
        currentPartName={currentPartName}
      />

      {/* Help Dialog */}
      <HelpDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Background Settings Dialog */}
      <BackgroundSettingsDialog
        open={showBackgroundSettings}
        onOpenChange={setShowBackgroundSettings}
        settings={backgroundSettings}
        onSettingsChange={setBackgroundSettings}
        onPreviewChange={setPreviewBackgroundSettings}
      />

      {/* Navigation Drawer */}
      <NavigationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNewFile={handleNewPart}
        onSave={handleSave}
        onSaveAs={handleSaveAs}
        onLoad={handleLoad}
        onExport={() => toast.info('Export SVG - Feature kommt bald!')}
        onImport={handleLoad}
        onOpenLibrary={() => {
          setShowLibrary(true);
          setDrawerOpen(false);
        }}
        onOpenHelp={() => {
          setShowHelp(true);
          setDrawerOpen(false);
        }}
        onOpenSettings={() => {
          setShowBackgroundSettings(true);
          setDrawerOpen(false);
        }}
      />

      {/* Connection Dialog */}
      <ConnectionDialog
        isOpen={isConnectionDialogOpen}
        label={tempConnection?.label || ''}
        onLabelChange={(label) => setTempConnection(prev => prev ? { ...prev, label } : null)}
        onConfirm={handleConnectionConfirm}
        onCancel={handleConnectionCancel}
      />

      {/* Text Dialog */}
      <TextDialog
        isOpen={tempPoints.length > 0 && currentTool === 'text'}
        onConfirm={handleTextConfirm}
        onCancel={handleTextCancel}
        initialText="Text"
        attributes={attributes}
      />
    </div>
  );
}